import { confirmNeed, isPencilledVisit } from './planning'
import { countsAsDayVisit, dayVisitCounts } from './day-visits'
import { splitAccessWarning, todayISO } from './ui'
import type { Clean, Customer, SchedulePayload } from './types'

export type DayState = 'done' | 'agreed' | 'pencilled' | 'open'
export const DAY_STATE_LABELS: Record<DayState, string> = { done: 'Done', agreed: 'Agreed', pencilled: 'Pencilled', open: 'Open' }
export interface DayStateOptions { date?: string; today?: string; closed?: boolean }
export function closedDiaryDay(p: Pick<SchedulePayload, 'finished_days' | 'day_ledger'> | null, date: string): boolean {
  return !!(p?.finished_days?.some(c => c.date === date) || p?.day_ledger?.some(c => c.date === date))
}
function standing(c: Clean): Parameters<typeof confirmNeed>[1] {
  return { status: c.customer_status as Customer['status'], customer_list: c.customer_list,
    messages_shelf: c.customer_messages_shelf, message_reply: c.customer_message_reply, follow_up_on: c.customer_follow_up_on }
}
// A saved close never hides an unanswered real job, even one with no worker or time.
// Blocked/reviewed work is already answered; none of it becomes a fictional completed job.
function leftAfterClose(c: Clean): boolean {
  return c.state === 'booked' && !c.blocked_reason && !c.reviewed_on
}
export function dayState(visits: readonly Clean[], options: DayStateOptions = {}): { state: DayState; done: number; total: number; toCheck: boolean; closed: boolean } {
  const counted = visits.filter(countsAsDayVisit), { done, total } = dayVisitCounts(counted)
  const today = options.today ?? todayISO(), date = options.date ?? counted[0]?.date ?? visits[0]?.date
  const past = !!date && date < today
  const closed = past && !!options.closed && !counted.some(leftAfterClose)
  // A past colour records the old agreement, with an amber review dot. It is not a live
  // Pencilled count. A closed day can be settled without pretending every job was done.
  const state: DayState = closed || total > 0 && done === total ? 'done' : !total ? 'open' : past
    ? counted.every(c => c.state === 'done' || !!c.confirmed) ? 'agreed' : 'pencilled'
    : counted.some(c => isPencilledVisit(c, undefined, today)) ? 'pencilled'
      : counted.every(c => c.state === 'done' || !!c.confirmed) ? 'agreed' : 'open'
  const toCheck = !closed && (past && total > done || counted.some(c => c.state === 'booked' &&
    (!!c.blocked_reason || !!splitAccessWarning(c.customer_address ?? '').warn || confirmNeed(c, standing(c), today) === 'check')))
  return { state, done, total, toCheck, closed }
}
// The day's figure (Liam, 3 Nov 2026: "the 0/9 of nine should fill up to those that confirmed that makes more sense for
// the diary page where you are booking stuff in"): while a day is still being booked (today or later, nothing done yet)
// it counts the visits agreed; once work starts, or the day has gone, it counts the visits done.
export function dayFigure(visits: readonly Clean[], options: DayStateOptions = {}): { shown: number; total: number; counting: 'done' | 'agreed' } {
  const counted = visits.filter(countsAsDayVisit), { done, total } = dayVisitCounts(counted)
  const today = options.today ?? todayISO(), date = options.date ?? counted[0]?.date ?? visits[0]?.date
  const past = !!date && date < today
  if (past || done > 0) return { shown: done, total, counting: 'done' }
  return { shown: counted.filter(c => c.state === 'done' || !!c.confirmed).length, total, counting: 'agreed' }
}
export function visitColour(c: Clean, today = todayISO()): DayState | null {
  if (!countsAsDayVisit(c)) return null
  if (c.state === 'done') return 'done'
  if (c.confirmed) return 'agreed'
  return isPencilledVisit(c, undefined, today) ? 'pencilled' : null
}
